import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Stack, Typography, Button } from "@mui/material";
import "./style.css";
import { useDispatch, useSelector } from "react-redux";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";
import { editAdminCMSPagesAction } from "../../redux/Actions";
import { Toast } from "../../component/Toast";
import {
  isEmpty,
  newEmailValidation,
  urlValidation,
} from "../../services/validation";
import { strings, paths } from "../../utils/constants";
import CustomTextField from "../../component/CustomTextField";
import PromotionItemsEditor from "../../component/PromotionItemsEditor";

function EditCMSPage() {
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const { isEditCMSPages, editCMSPagesData } = useSelector(
    (state) => state.adminCMSPagesReducer
  );

  // Strip HTML tags/entities so an "empty" rich-text editor (which yields
  // markup like "<p><br></p>") is correctly treated as empty during validation.
  const stripHtml = (html) =>
    (html || "")
      .replace(/<[^>]*>/g, "")
      .replace(/&nbsp;/g, " ")
      .trim();

  // The ada-title content is stored as a PLAIN string (not Quill HTML). Feeding
  // a raw string straight into ReactQuill's controlled `value` makes the editor
  // fight the user's keystrokes (edits appear to revert / can't type). Convert a
  // plain string into the HTML Quill itself produces (<p> with <br> for line
  // breaks) so the controlled value matches Quill's internal output. If the
  // value already looks like HTML, leave it as-is.
  const toQuillHtml = (value) => {
    const str = value || "";
    if (!str) return "";
    if (/<[a-z][\s\S]*>/i.test(str)) return str; // already HTML
    return `<p>${str
      .replace(/&/g, "&amp;")
      .replace(/</g, "&lt;")
      .replace(/>/g, "&gt;")
      .replace(/\r?\n/g, "<br>")}</p>`;
  };

  // Helper function to safely parse JSON content
  const safeParseContent = (content) => {
    try {
      if (!content || content === "") {
        return {};
      }
      return JSON.parse(content);
    } catch (error) {
      console.error("Error parsing content:", error);
      return {};
    }
  };

  const [id] = useState(isEditCMSPages ? editCMSPagesData?.id : "");

  const [title, setTitle] = useState(
    isEditCMSPages && editCMSPagesData?.title != null
      ? editCMSPagesData?.title
      : ""
  );

  const [titleInFrench, setTitleInFrench] = useState(
    isEditCMSPages && editCMSPagesData?.title_fr != null
      ? editCMSPagesData?.title_fr
      : ""
  );

  const [address, setAddress] = useState(
    isEditCMSPages &&
      editCMSPagesData?.key === strings.cmsPages.CONTACT &&
      JSON.parse(editCMSPagesData?.content).address != null
      ? JSON.parse(editCMSPagesData?.content).address
      : ""
  );

  const [addressInFrench, setAddressInFrench] = useState(
    isEditCMSPages &&
      editCMSPagesData?.key === strings.cmsPages.CONTACT &&
      JSON.parse(editCMSPagesData?.content).address_fr != null
      ? JSON.parse(editCMSPagesData?.content).address_fr
      : ""
  );

  const [email, setEmail] = useState(
    isEditCMSPages &&
      editCMSPagesData?.key === strings.cmsPages.CONTACT &&
      JSON.parse(editCMSPagesData?.content).email != null
      ? JSON.parse(editCMSPagesData?.content).email
      : ""
  );

  const [fbLink] = useState(
    isEditCMSPages &&
      editCMSPagesData?.key === strings.cmsPages.CONTACT &&
      JSON.parse(editCMSPagesData?.content).fbLink != null
      ? JSON.parse(editCMSPagesData?.content).fbLink
      : ""
  );

  const [instagramLink] = useState(
    isEditCMSPages &&
      editCMSPagesData?.key === strings.cmsPages.CONTACT &&
      JSON.parse(editCMSPagesData?.content).instagramLink != null
      ? JSON.parse(editCMSPagesData?.content).instagramLink
      : ""
  );

  const [twitterLink] = useState(
    isEditCMSPages &&
      editCMSPagesData?.key === strings.cmsPages.CONTACT &&
      JSON.parse(editCMSPagesData?.content).twitterLink != null
      ? JSON.parse(editCMSPagesData?.content).twitterLink
      : ""
  );

  const [aboutUsdDescritpion, setAboutUsdDescritpion] = useState(
    isEditCMSPages &&
      editCMSPagesData?.key !== strings.cmsPages.CONTACT &&
      editCMSPagesData?.key !== strings.cmsPages.ADA_TITLE &&
      editCMSPagesData?.key !== strings.cmsPages.ADA_SUBTITLE &&
      JSON.parse(editCMSPagesData?.content).description != null
      ? JSON.parse(editCMSPagesData?.content).description
      : ""
  );

  const [aboutUsdDescritpionInFrench, setAboutUsdDescritpionInFrench] =
    useState(
      isEditCMSPages &&
        editCMSPagesData?.key !== strings.cmsPages.CONTACT &&
        editCMSPagesData?.key !== strings.cmsPages.ADA_TITLE &&
        editCMSPagesData?.key !== strings.cmsPages.ADA_SUBTITLE &&
        JSON.parse(editCMSPagesData?.content).description_fr != null
        ? JSON.parse(editCMSPagesData?.content).description_fr
        : ""
    );

  // ADA Title page stores its body in the top-level `content` / `content_fr`
  // columns as PLAIN (rich-HTML) strings — NOT a JSON-stringified object like
  // the other pages. So read them directly, without JSON.parse.
  const [adaTitleContent, setAdaTitleContent] = useState(
    isEditCMSPages &&
      editCMSPagesData?.key === strings.cmsPages.ADA_TITLE &&
      editCMSPagesData?.content != null
      ? toQuillHtml(editCMSPagesData?.content)
      : ""
  );

  const [adaTitleContentFr, setAdaTitleContentFr] = useState(
    isEditCMSPages &&
      editCMSPagesData?.key === strings.cmsPages.ADA_TITLE &&
      editCMSPagesData?.content_fr != null
      ? toQuillHtml(editCMSPagesData?.content_fr)
      : ""
  );

  // ADA Subtitle — same storage shape as ADA Title: plain rich-HTML strings in
  // the top-level content / content_fr columns (not JSON).
  const [adaSubtitleContent, setAdaSubtitleContent] = useState(
    isEditCMSPages &&
      editCMSPagesData?.key === strings.cmsPages.ADA_SUBTITLE &&
      editCMSPagesData?.content != null
      ? toQuillHtml(editCMSPagesData?.content)
      : ""
  );

  const [adaSubtitleContentFr, setAdaSubtitleContentFr] = useState(
    isEditCMSPages &&
      editCMSPagesData?.key === strings.cmsPages.ADA_SUBTITLE &&
      editCMSPagesData?.content_fr != null
      ? toQuillHtml(editCMSPagesData?.content_fr)
      : ""
  );

  const [aboutUsImageLink, setAboutUsImageLink] = useState(
    isEditCMSPages &&
      editCMSPagesData?.key === strings.cmsPages.ABOUT &&
      JSON.parse(editCMSPagesData?.content).imageLink != null
      ? JSON.parse(editCMSPagesData?.content).imageLink
      : ""
  );

  const [videoLink, setVideoLink] = useState(
    isEditCMSPages &&
      editCMSPagesData?.key === strings.cmsPages.ABOUT &&
      JSON.parse(editCMSPagesData?.content).videoLink != null
      ? JSON.parse(editCMSPagesData?.content).videoLink
      : ""
  );

  // Promotion items state (separate arrays for English and French)
  const [promotionItems, setPromotionItems] = useState(() => {
    if (
      isEditCMSPages &&
      editCMSPagesData?.key === strings.cmsPages.PROMOTION &&
      editCMSPagesData?.content
    ) {
      try {
        const content = safeParseContent(editCMSPagesData.content);
        return content.description || [];
      } catch (error) {
        return [];
      }
    }
    return [];
  });

  const [promotionItemsFr, setPromotionItemsFr] = useState(() => {
    if (
      isEditCMSPages &&
      editCMSPagesData?.key === strings.cmsPages.PROMOTION &&
      editCMSPagesData?.content
    ) {
      try {
        const content = safeParseContent(editCMSPagesData.content);
        return content.description_fr || [];
      } catch (error) {
        return [];
      }
    }
    return [];
  });

  const handlePromotionItemsChange = (newItems, newItemsFr) => {
    setPromotionItems(newItems);
    setPromotionItemsFr(newItemsFr);
  };

  const submitHandler = async (e) => {
    e.preventDefault();
    if (isEmpty(title)) {
      Toast({
        type: "error",
        title: strings.words.ERROR,
        message: strings.admin.error.PAGE_TITLE_ENGLISH_REQUIRED,
      });
    } else if (isEmpty(titleInFrench)) {
      Toast({
        type: "error",
        title: strings.words.ERROR,
        message: strings.admin.error.PAGE_TITLE_FRENCH_REQUIRED,
      });
    } else if (editCMSPagesData?.key === strings.cmsPages.CONTACT && isEmpty(address)) {
      Toast({
        type: "error",
        title: strings.words.ERROR,
        message: strings.admin.error.ADDRESS_ENGLISH_REQUIRED,
      });
    } else if (editCMSPagesData?.key === strings.cmsPages.CONTACT && isEmpty(addressInFrench)) {
      Toast({
        type: "error",
        title: strings.words.ERROR,
        message: strings.admin.error.ADDRESS_FRENCH_REQUIRED,
      });
    } else if (editCMSPagesData?.key === strings.cmsPages.CONTACT && isEmpty(email)) {
      Toast({
        type: "error",
        title: strings.words.ERROR,
        message: strings.admin.error.EMAIL_REQUIRED,
      });
    } else if (
      editCMSPagesData?.key === strings.cmsPages.CONTACT &&
      !newEmailValidation(email)
    ) {
      Toast({
        type: "error",
        title: strings.words.ERROR,
        message: strings.admin.error.EMAIL_VALIDATION,
      });
    } else if (editCMSPagesData?.key === strings.cmsPages.CONTACT && isEmpty(fbLink)) {
      Toast({
        type: "error",
        title: strings.words.ERROR,
        message: strings.admin.error.FB_LINK_REQUIRED,
      });
    } else if (editCMSPagesData?.key === strings.cmsPages.CONTACT && !urlValidation(fbLink)) {
      Toast({
        type: "error",
        title: strings.words.ERROR,
        message: strings.admin.error.FB_LINK_VALIDATION,
      });
    } else if (editCMSPagesData?.key === strings.cmsPages.CONTACT && isEmpty(instagramLink)) {
      Toast({
        type: "error",
        title: strings.words.ERROR,
        message: strings.admin.error.INSTAGRAM_LINK_REQUIRED,
      });
    } else if (
      editCMSPagesData?.key === strings.cmsPages.CONTACT &&
      !urlValidation(instagramLink)
    ) {
      Toast({
        type: "error",
        title: strings.words.ERROR,
        message: strings.admin.error.INSTAGRAM_LINK_VALIDATION,
      });
    } else if (editCMSPagesData?.key === strings.cmsPages.CONTACT && isEmpty(twitterLink)) {
      Toast({
        type: "error",
        title: strings.words.ERROR,
        message: strings.admin.error.TWITTER_LINK_REQUIRED,
      });
    } else if (
      editCMSPagesData?.key === strings.cmsPages.CONTACT &&
      !urlValidation(twitterLink)
    ) {
      Toast({
        type: "error",
        title: strings.words.ERROR,
        message: strings.admin.error.TWITTER_LINK_VALIDATION,
      });
    } else if (
      editCMSPagesData?.key !== strings.cmsPages.CONTACT &&
      editCMSPagesData?.key !== strings.cmsPages.PROMOTION &&
      editCMSPagesData?.key !== strings.cmsPages.ADA_TITLE &&
      editCMSPagesData?.key !== strings.cmsPages.ADA_SUBTITLE &&
      isEmpty(aboutUsdDescritpion)
    ) {
      Toast({
        type: "error",
        title: strings.words.ERROR,
        message: strings.admin.error.DESCRIPTION_ENGLISH_REQUIRED,
      });
    } else if (
      editCMSPagesData?.key !== strings.cmsPages.CONTACT &&
      editCMSPagesData?.key !== strings.cmsPages.PROMOTION &&
      editCMSPagesData?.key !== strings.cmsPages.ADA_TITLE &&
      editCMSPagesData?.key !== strings.cmsPages.ADA_SUBTITLE &&
      isEmpty(aboutUsdDescritpionInFrench)
    ) {
      Toast({
        type: "error",
        title: strings.words.ERROR,
        message: strings.admin.error.DESCRIPTION_FRENCH_REQUIRED,
      });
    } else if (
      editCMSPagesData?.key === strings.cmsPages.ABOUT &&
      isEmpty(aboutUsImageLink)
    ) {
      Toast({
        type: "error",
        title: strings.words.ERROR,
        message: strings.admin.error.IMAGE_LINK_REQUIRED,
      });
    } else if (
      editCMSPagesData?.key === strings.cmsPages.ABOUT &&
      !urlValidation(aboutUsImageLink)
    ) {
      Toast({
        type: "error",
        title: strings.words.ERROR,
        message: strings.admin.error.IMAGE_LINK_VALIDATION,
      });
    } else if (
      editCMSPagesData?.key === strings.cmsPages.ABOUT &&
      isEmpty(videoLink)
    ) {
      Toast({
        type: "error",
        title: strings.words.ERROR,
        message: strings.admin.error.VIDEO_LINK_REQUIRED,
      });
    } else if (
      editCMSPagesData?.key === strings.cmsPages.ABOUT &&
      !urlValidation(videoLink)
    ) {
      Toast({
        type: "error",
        title: strings.words.ERROR,
        message: strings.admin.error.VIDEO_LINK_VALIDATION,
      });
    } else if (
      editCMSPagesData?.key === strings.cmsPages.ADA_TITLE &&
      isEmpty(stripHtml(adaTitleContent))
    ) {
      Toast({
        type: "error",
        title: strings.words.ERROR,
        message: strings.admin.error.DESCRIPTION_ENGLISH_REQUIRED,
      });
    } else if (
      editCMSPagesData?.key === strings.cmsPages.ADA_TITLE &&
      isEmpty(stripHtml(adaTitleContentFr))
    ) {
      Toast({
        type: "error",
        title: strings.words.ERROR,
        message: strings.admin.error.DESCRIPTION_FRENCH_REQUIRED,
      });
    } else if (
      editCMSPagesData?.key === strings.cmsPages.ADA_SUBTITLE &&
      isEmpty(stripHtml(adaSubtitleContent))
    ) {
      Toast({
        type: "error",
        title: strings.words.ERROR,
        message: strings.admin.error.DESCRIPTION_ENGLISH_REQUIRED,
      });
    } else if (
      editCMSPagesData?.key === strings.cmsPages.ADA_SUBTITLE &&
      isEmpty(stripHtml(adaSubtitleContentFr))
    ) {
      Toast({
        type: "error",
        title: strings.words.ERROR,
        message: strings.admin.error.DESCRIPTION_FRENCH_REQUIRED,
      });
    } else {
      let requestInfo;

      if (editCMSPagesData?.key === strings.cmsPages.ADA_TITLE) {
        // ADA Title keeps its body in the top-level content / content_fr
        // columns as plain rich-HTML strings (no JSON wrapping).
        requestInfo = {
          id: id,
          title: title,
          title_fr: titleInFrench,
          content: adaTitleContent,
          content_fr: adaTitleContentFr,
        };
      } else if (editCMSPagesData?.key === strings.cmsPages.ADA_SUBTITLE) {
        // ADA Subtitle uses the same top-level content / content_fr storage.
        requestInfo = {
          id: id,
          title: title,
          title_fr: titleInFrench,
          content: adaSubtitleContent,
          content_fr: adaSubtitleContentFr,
        };
      } else if (editCMSPagesData?.key === strings.cmsPages.CONTACT) {
        let content = JSON.stringify({
          address: address,
          address_fr: addressInFrench,
          email: email,
          fbLink: fbLink,
          instagramLink: instagramLink,
          twitterLink: twitterLink,
        });

        requestInfo = {
          id: id,
          title: title,
          content: content,
          title_fr: titleInFrench,
        };
      } else if (
        editCMSPagesData?.key === strings.cmsPages.TERMS ||
        editCMSPagesData?.key === strings.cmsPages.DEMO
      ) {
        let content = JSON.stringify({
          description: aboutUsdDescritpion,
          description_fr: aboutUsdDescritpionInFrench,
        });

        requestInfo = {
          id: id,
          title: title,
          content: content,
          title_fr: titleInFrench,
        };
      } else if (editCMSPagesData?.key === strings.cmsPages.PROMOTION) {
        let content = JSON.stringify({
          description: promotionItems,
          description_fr: promotionItemsFr,
        });

        requestInfo = {
          id: id,
          title: title,
          content: content,
          title_fr: titleInFrench,
        };
      } else {
        let content = JSON.stringify({
          description: aboutUsdDescritpion,
          description_fr: aboutUsdDescritpionInFrench,
          imageLink: aboutUsImageLink,
          videoLink: videoLink,
        });

        requestInfo = {
          id: id,
          title: title,
          content: content,
          title_fr: titleInFrench,
        };
      }
      let editCMSPagesResponse = await dispatch(
        editAdminCMSPagesAction({ language: strings.language.EN, requestInfo })
      );
      if (editCMSPagesResponse.payload?.success) {
        navigate(paths.adminRoutes.CMS_PAGES, { replace: true });
      }
    }
  };

  const titleChangeEnHandler = (e) => {
    setTitle(e.target.value);
  };
  const titleChangeFrHandler = (e) => {
    setTitleInFrench(e.target.value);
  };
  const addressChangeEnHandler = (e) => {
    setAddress(e.target.value);
  };
  const addressChangeFrHandler = (e) => {
    setAddressInFrench(e.target.value);
  };
  const emailChangeHandler = (e) => {
    setEmail(e.target.value);
  };
  // const fbLinkChangeHandler = (e) => {
  //   setFBLink(e.target.value);
  // };
  // const instaLinkChageHandler = (e) => {
  //   setInstagramLink(e.target.value);
  // };
  // const twitterLinkChangeHandler = (e) => {
  //   setTwitterLink(e.target.value);
  // };

  const aboutUsdDescritpionEnHandler = (value) => {
    setAboutUsdDescritpion(value);
  };
  const aboutUsdDescritpionFrHandler = (value) => {
    setAboutUsdDescritpionInFrench(value);
  };

  const adaTitleContentEnHandler = (value) => {
    setAdaTitleContent(value);
  };
  const adaTitleContentFrHandler = (value) => {
    setAdaTitleContentFr(value);
  };

  const adaSubtitleContentEnHandler = (value) => {
    setAdaSubtitleContent(value);
  };
  const adaSubtitleContentFrHandler = (value) => {
    setAdaSubtitleContentFr(value);
  };

  // Quill editor configuration for Terms of Use and Demo
  const quillModules = {
    toolbar: [
      [{ header: [1, 2, 3, 4, 5, 6, false] }],
      ["bold", "italic", "underline", "strike"],
      [
        {
          color: [
            "#184437", // System primary color (green)
            "#000000", // Black
            "#ffffff", // White
            "#666666", // Gray
            "#e60000", // Red
            "#0066cc", // Blue
            "#ff9900", // Orange
            "#008a00", // Green
          ],
        },
        {
          background: [
            "#184437", // System primary color (green)
            "#000000", // Black
            "#ffffff", // White
            "#f5f5f5", // Light gray
            "#ffffcc", // Light yellow
            "#cce8cc", // Light green
            "#cce0f5", // Light blue
            "#ffebcc", // Light orange
          ],
        },
      ],
      [{ list: "ordered" }, { list: "bullet" }],
      [{ indent: "-1" }, { indent: "+1" }],
      [{ align: [] }],
      ["link", "video"],
      ["clean"],
    ],
  };

  const quillFormats = [
    "header",
    "bold",
    "italic",
    "underline",
    "strike",
    "color",
    "background",
    "list",
    "bullet",
    "indent",
    "align",
    "link",
    "video",
  ];
  const imageLinkChangeHandler = (e) => {
    setAboutUsImageLink(e.target.value);
  };
  const videoLinkChangeHandler = (e) => {
    setVideoLink(e.target.value);
  };
  const cancelHandler = () => {
    navigate(paths.adminRoutes.CMS_PAGES, { replace: true });
  };
  return (
    <div>
      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        mb={5}
      >
        <Typography
          variant="h4"
          gutterBottom
          sx={{ marginLeft: 5, marginTop: 5 }}
        >
          {editCMSPagesData?.key === strings.cmsPages.CONTACT
            ? strings.general.EDIT_CONTACT_US
            : editCMSPagesData?.key === strings.cmsPages.ABOUT
              ? strings.general.EDIT_ABOUT_US
              : editCMSPagesData?.key === strings.cmsPages.TERMS
                ? strings.general.EDIT_TERMS_OF_USE
                : editCMSPagesData?.key === strings.cmsPages.DEMO
                  ? strings.general.EDIT_DEMO
                  : editCMSPagesData?.key === strings.cmsPages.PROMOTION
                    ? strings.general.EDIT_PROMOTION
                    : editCMSPagesData?.key === strings.cmsPages.ADA_TITLE
                      ? strings.general.EDIT_ADA_TITLE
                      : editCMSPagesData?.key === strings.cmsPages.ADA_SUBTITLE
                        ? strings.general.EDIT_ADA_SUBTITLE
                        : strings.general.CMS_PAGE}
        </Typography>
      </Stack>

      <form
        style={{
          marginBottom: "5px",
          marginLeft: "50px",
          marginRight: "50px",
          paddingBottom: "20px",
        }}
      >
        <CustomTextField
          style={{
            width: "100%",
            marginTop: "5px",
            marginBottom: "5px",
            marginLeft: "5px",
            marginRight: "5px",
          }}
          type="text"
          label={`${strings.general.PAGE_TITLE_ENGLISH}*`}
          variant="outlined"
          focusColor="#184437"
          value={title}
          autoFocus
          autoComplete="off"
          inputProps={{ maxLength: 150 }}
          onChange={titleChangeEnHandler}
        />
        <br />
        <CustomTextField
          style={{
            width: "100%",
            marginTop: "5px",
            marginBottom: "5px",
            marginLeft: "5px",
            marginRight: "5px",
          }}
          type="text"
          label={`${strings.general.PAGE_TITLE_FRENCH}*`}
          variant="outlined"
          focusColor="#184437"
          value={titleInFrench}
          autoComplete="off"
          inputProps={{ maxLength: 150 }}
          onChange={titleChangeFrHandler}
        />
        <br />
        {editCMSPagesData?.key === strings.cmsPages.CONTACT ? (
          <>
            <CustomTextField
              style={{
                width: "100%",
                marginTop: "10px",
                marginBottom: "5px",
                marginLeft: "5px",
                marginRight: "5px",
              }}
              type="text"
              label={`${strings.words.ADDRESS}*`}
              variant="outlined"
              focusColor="#184437"
              value={address}
              autoFocus
              autoComplete="off"
              inputProps={{ maxLength: 150 }}
              onChange={addressChangeEnHandler}
            />
            <br />
            <CustomTextField
              style={{
                width: "100%",
                marginTop: "10px",
                marginBottom: "5px",
                marginLeft: "5px",
                marginRight: "5px",
              }}
              type="text"
              label={`${strings.words.ADRESSE}*`}
              variant="outlined"
              focusColor="#184437"
              value={addressInFrench}
              autoFocus
              autoComplete="off"
              inputProps={{ maxLength: 150 }}
              onChange={addressChangeFrHandler}
            />
            <br />
            <CustomTextField
              style={{
                width: "100%",
                marginTop: "10px",
                marginBottom: "5px",
                marginLeft: "5px",
                marginRight: "5px",
              }}
              type="text"
              label={`${strings.words.EMAIL}*`}
              variant="outlined"
              focusColor="#184437"
              value={email}
              autoFocus
              autoComplete="off"
              inputProps={{ maxLength: 150 }}
              onChange={emailChangeHandler}
            />
            {/* <br />
            <CustomTextField
              style={{
                width: "100%",
                marginTop: "10px",
                marginBottom: "5px",
                marginLeft: "5px",
                marginRight: "5px",
              }}
              type="text"
              label={`${strings.general.FB_LINK}*`}
              variant="outlined"
              focusColor="#184437"
              value={fbLink}
              autoFocus
              autoComplete="off"
              inputProps={{ maxLength: 150 }}
              onChange={fbLinkChangeHandler}
            />
            <br />
            <CustomTextField
              style={{
                width: "100%",
                marginTop: "10px",
                marginBottom: "5px",
                marginLeft: "5px",
                marginRight: "5px",
              }}
              type="text"
              label={`${strings.general.INSTAGRAM_LINK}*`}
              variant="outlined"
              focusColor="#184437"
              value={instagramLink}
              autoFocus
              autoComplete="off"
              inputProps={{ maxLength: 150 }}
              onChange={instaLinkChageHandler}
            />
            <br />
            <CustomTextField
              style={{
                width: "100%",
                marginTop: "10px",
                marginBottom: "5px",
                marginLeft: "5px",
                marginRight: "5px",
              }}
              type="text"
              label={`${strings.general.TWITTER_LINK}*`}
              variant="outlined"
              focusColor="#184437"
              value={twitterLink}
              autoFocus
              autoComplete="off"
              inputProps={{ maxLength: 150 }}
              onChange={twitterLinkChangeHandler}
            />
            <br /> */}
          </>
        ) : editCMSPagesData?.key === strings.cmsPages.TERMS ||
          editCMSPagesData?.key === strings.cmsPages.DEMO ? (
          <>
            <div style={{ marginTop: "10px", marginBottom: "5px" }}>
              <label
                style={{
                  display: "block",
                  marginBottom: "8px",
                  marginLeft: "5px",
                  color: "#666",
                  fontSize: "14px",
                }}
              >
                {strings.words.DESCRIPTION}*
              </label>
              <ReactQuill
                theme="snow"
                value={aboutUsdDescritpion}
                onChange={aboutUsdDescritpionEnHandler}
                modules={quillModules}
                formats={quillFormats}
                style={{
                  marginLeft: "5px",
                  marginRight: "5px",
                  backgroundColor: "white",
                }}
              />
            </div>
            <br />
            <div style={{ marginTop: "10px", marginBottom: "5px" }}>
              <label
                style={{
                  display: "block",
                  marginBottom: "8px",
                  marginLeft: "5px",
                  color: "#666",
                  fontSize: "14px",
                }}
              >
                {strings.general.DESCRIPTION_IN_FRENCH}*
              </label>
              <ReactQuill
                theme="snow"
                value={aboutUsdDescritpionInFrench}
                onChange={aboutUsdDescritpionFrHandler}
                modules={quillModules}
                formats={quillFormats}
                style={{
                  marginLeft: "5px",
                  marginRight: "5px",
                  backgroundColor: "white",
                }}
              />
            </div>
            <br />
          </>
        ) : editCMSPagesData?.key === strings.cmsPages.ADA_TITLE ? (
          <>
            <div style={{ marginTop: "10px", marginBottom: "5px" }}>
              <label
                style={{
                  display: "block",
                  marginBottom: "8px",
                  marginLeft: "5px",
                  color: "#666",
                  fontSize: "14px",
                }}
              >
                {strings.words.DESCRIPTION}*
              </label>
              <ReactQuill
                theme="snow"
                value={adaTitleContent}
                onChange={adaTitleContentEnHandler}
                modules={quillModules}
                formats={quillFormats}
                style={{
                  marginLeft: "5px",
                  marginRight: "5px",
                  backgroundColor: "white",
                }}
              />
            </div>
            <br />
            <div style={{ marginTop: "10px", marginBottom: "5px" }}>
              <label
                style={{
                  display: "block",
                  marginBottom: "8px",
                  marginLeft: "5px",
                  color: "#666",
                  fontSize: "14px",
                }}
              >
                {strings.general.DESCRIPTION_IN_FRENCH}*
              </label>
              <ReactQuill
                theme="snow"
                value={adaTitleContentFr}
                onChange={adaTitleContentFrHandler}
                modules={quillModules}
                formats={quillFormats}
                style={{
                  marginLeft: "5px",
                  marginRight: "5px",
                  backgroundColor: "white",
                }}
              />
            </div>
            <br />
          </>
        ) : editCMSPagesData?.key === strings.cmsPages.ADA_SUBTITLE ? (
          <>
            <div style={{ marginTop: "10px", marginBottom: "5px" }}>
              <label
                style={{
                  display: "block",
                  marginBottom: "8px",
                  marginLeft: "5px",
                  color: "#666",
                  fontSize: "14px",
                }}
              >
                {strings.words.DESCRIPTION}*
              </label>
              <ReactQuill
                theme="snow"
                value={adaSubtitleContent}
                onChange={adaSubtitleContentEnHandler}
                modules={quillModules}
                formats={quillFormats}
                style={{
                  marginLeft: "5px",
                  marginRight: "5px",
                  backgroundColor: "white",
                }}
              />
            </div>
            <br />
            <div style={{ marginTop: "10px", marginBottom: "5px" }}>
              <label
                style={{
                  display: "block",
                  marginBottom: "8px",
                  marginLeft: "5px",
                  color: "#666",
                  fontSize: "14px",
                }}
              >
                {strings.general.DESCRIPTION_IN_FRENCH}*
              </label>
              <ReactQuill
                theme="snow"
                value={adaSubtitleContentFr}
                onChange={adaSubtitleContentFrHandler}
                modules={quillModules}
                formats={quillFormats}
                style={{
                  marginLeft: "5px",
                  marginRight: "5px",
                  backgroundColor: "white",
                }}
              />
            </div>
            <br />
          </>
        ) : editCMSPagesData?.key === strings.cmsPages.PROMOTION ? (
          <div style={{ marginTop: "20px", marginBottom: "20px" }}>
            <PromotionItemsEditor
              items={promotionItems}
              itemsFr={promotionItemsFr}
              onChange={handlePromotionItemsChange}
            />
          </div>
        ) : (
          <>
            <CustomTextField
              style={{
                width: "100%",
                marginTop: "10px",
                marginBottom: "5px",
                marginLeft: "5px",
                marginRight: "5px",
              }}
              type="text"
              label={`${strings.words.DESCRIPTION}*`}
              variant="outlined"
              focusColor="#184437"
              value={aboutUsdDescritpion}
              autoComplete="off"
              onChange={(e) => aboutUsdDescritpionEnHandler(e.target.value)}
            />
            <br />
            <CustomTextField
              style={{
                width: "100%",
                marginTop: "10px",
                marginBottom: "5px",
                marginLeft: "5px",
                marginRight: "5px",
              }}
              type="text"
              label={`${strings.general.DESCRIPTION_IN_FRENCH}*`}
              variant="outlined"
              focusColor="#184437"
              value={aboutUsdDescritpionInFrench}
              autoComplete="off"
              onChange={(e) => aboutUsdDescritpionFrHandler(e.target.value)}
            />
            <br />
            <CustomTextField
              style={{
                width: "100%",
                marginTop: "10px",
                marginBottom: "5px",
                marginLeft: "5px",
                marginRight: "5px",
              }}
              type="text"
              label={`${strings.general.IMAGE_LINK}*`}
              variant="outlined"
              focusColor="#184437"
              value={aboutUsImageLink}
              autoComplete="off"
              onChange={imageLinkChangeHandler}
            />
            <br />
            <CustomTextField
              style={{
                width: "100%",
                marginTop: "10px",
                marginBottom: "5px",
                marginLeft: "5px",
                marginRight: "5px",
              }}
              type="text"
              label={`${strings.general.VIDEO_LINK}*`}
              variant="outlined"
              focusColor="#184437"
              value={videoLink}
              autoComplete="off"
              onChange={videoLinkChangeHandler}
            />
          </>
        )}
        <Stack
          direction={{ xs: "column", sm: "row" }}
          alignItems="start"
          justifyContent="left"
          style={{ paddingBottom: "20px", marginTop: 20 }}
        >
          <Button
            variant="contained"
            color="primary"
            style={{
              backgroundColor: "#184437",
              marginTop: 5,
              marginRight: "12px",
            }}
            onClick={submitHandler}
          >
            {strings.words.SUBMIT}
          </Button>
          <Button
            variant="contained"
            color="primary"
            style={{ backgroundColor: "#184437", marginTop: 5 }}
            onClick={cancelHandler}
          >
            {strings.words.CANCEL}
          </Button>
        </Stack>
      </form>
    </div>
  );
}

export default EditCMSPage;
